import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCircle, MailCheck, MailWarning, ShieldCheck, Loader2, FileText, IdCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Loading } from "../components/Loading";
import { getAccount, resendVerification, acceptCurrentTerms, updateProfile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function Account() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [authState, setAuthState] = useState("checking"); // checking | ok | unauth
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);

  const load = () => getAccount().then(setData).catch(() => {});

  // Server-authoritative check: verify the session by calling the API directly
  // (sends the bearer token and/or the session cookie). Only a real 401 signs
  // the user out — this avoids transient auth-context races on mount.
  useEffect(() => {
    let alive = true;
    getAccount()
      .then((d) => { if (alive) { setData(d); setAuthState("ok"); } })
      .catch((e) => {
        if (!alive) return;
        if (e?.response?.status === 401) setAuthState("unauth");
        else setAuthState("ok"); // transient/network error: don't sign out
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (authState === "unauth") navigate("/login", { state: { from: "/account" } });
  }, [authState, navigate]);

  useEffect(() => {
    if (data?.user?.name != null) setNameInput(data.user.name);
  }, [data]);

  if (authState === "checking" || (authState === "ok" && !data)) return <Loading label="Loading your account…" />;
  if (authState === "unauth" || !data) return null;

  const u = data.user;

  const saveName = async () => {
    const next = nameInput.trim();
    if (!next) { toast.error("Display name cannot be empty."); return; }
    setSavingName(true);
    try {
      const d = await updateProfile(next);
      setUser(d.user);
      setData((prev) => ({ ...prev, user: d.user }));
      toast.success("Display name updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update name.");
    } finally { setSavingName(false); }
  };

  const resend = async () => {
    setBusy(true);
    try { const d = await resendVerification(u.email); setNote(d.message); } finally { setBusy(false); }
  };
  const reaccept = async () => {
    setBusy(true);
    try { const d = await acceptCurrentTerms(); setUser(d.user); await load(); setNote(d.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-12" data-testid="account-page">
      <div className="flex items-center gap-3 mb-8">
        <UserCircle className="h-9 w-9 text-atlas-blue" />
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">My account</h1>
          <p className="text-muted-foreground">{u.name} · {u.email}</p>
        </div>
      </div>

      {/* Display name (signs shared reports) */}
      <div className="bg-card border border-border rounded-md p-6 mb-6" data-testid="account-display-name">
        <div className="flex items-center gap-2 mb-3">
          <IdCard className="h-5 w-5 text-atlas-blue" />
          <h2 className="font-display text-lg font-semibold">Display name</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">Shown as “Prepared by …” on Atlas Opportunity Reports you share with dealers.</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            maxLength={80}
            placeholder="e.g. Jan de Vries"
            className="h-10 min-w-[240px] flex-1 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-atlas-blue/40"
            data-testid="account-name-input"
          />
          <Button
            onClick={saveName}
            disabled={savingName || nameInput.trim() === (u.name || "")}
            className="h-10 bg-atlas-blue hover:bg-atlas-sky text-white"
            data-testid="account-name-save"
          >
            {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save name"}
          </Button>
        </div>
      </div>

      {/* Email verification */}
      <div className="bg-card border border-border rounded-md p-6 mb-6" data-testid="account-verification">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {u.email_verified
              ? <MailCheck className="h-6 w-6 text-emerald-600" />
              : <MailWarning className="h-6 w-6 text-amber-600" />}
            <div>
              <div className="font-semibold">Email {u.email_verified ? "verified" : "not verified"}</div>
              <div className="text-sm text-muted-foreground">
                {u.email_verified ? `Verified ${fmt(u.email_verified_at)}` : "Verify your email to fully activate your account."}
              </div>
            </div>
          </div>
          {!u.email_verified && (
            <Button onClick={resend} disabled={busy} data-testid="account-resend-verification" variant="outline">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resend"}
            </Button>
          )}
        </div>
      </div>

      {/* Consent */}
      <div className="bg-card border border-border rounded-md p-6 mb-6" data-testid="account-consent">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-5 w-5 text-atlas-blue" />
          <h2 className="font-display text-lg font-semibold">Terms & privacy acceptance</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-md border border-border p-3">
            <div className="overline text-muted-foreground">Terms of Use</div>
            <div className="font-mono">{u.terms_version || "—"}</div>
            <div className="text-xs text-muted-foreground">Accepted {fmt(u.terms_accepted_at)}</div>
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="overline text-muted-foreground">Privacy Notice</div>
            <div className="font-mono">{u.privacy_version || "—"}</div>
            <div className="text-xs text-muted-foreground">Accepted {fmt(u.privacy_accepted_at)}</div>
          </div>
        </div>

        {data.needs_reacceptance && (
          <div className="mt-4 rounded-md border-2 border-amber-300 bg-amber-50 p-4" data-testid="account-reaccept">
            <p className="text-sm text-amber-900 mb-3">
              Our Terms (v{data.current_terms_version}) or Privacy Notice (v{data.current_privacy_version}) have been updated. Please review and accept the current versions.
            </p>
            <Button onClick={reaccept} disabled={busy} data-testid="account-accept-current" className="bg-primary text-white">
              Accept current Terms & Privacy
            </Button>
          </div>
        )}

        {note && <p className="text-sm text-emerald-700 mt-3">{note}</p>}

        <div className="mt-5">
          <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Consent history</div>
          <div className="space-y-1.5" data-testid="account-consent-history">
            {data.consent_history.map((r, i) => (
              <div key={i} className="flex justify-between text-xs font-mono text-slate-500 border-b border-border py-1.5">
                <span className="capitalize">{r.document} · {r.version}</span>
                <span>{fmt(r.accepted_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
